/**
 * Cliente de grupo e famílias.
 *
 * Escrita aqui vai pela rede, não pelo outbox — criar família, convidar e
 * aceitar dependem de checagens que só o servidor consegue fazer (nome único,
 * exclusividade de família, prazo do convite). Enfileirar offline daria à
 * pessoa a impressão de que entrou numa família que talvez recuse.
 */

const API = '/api/v1';

export interface FamilyView {
  id: string;
  name: string;
  color: string;
  memberIds: string[];
  /** Convites sem resposta: a família aparece como "Pendente". */
  pendingInvites: number;
  /** Quem está convidado e ainda não respondeu — não se convida de novo. */
  pendingInviteeIds: string[];
}

export interface PendingFamilyInvite {
  id: string;
  familyId: string;
  familyName: string;
  familyColor: string;
  expiresAt: string;
  daysLeft: number;
  /** Quem convidou — para a Home dizer "[nome] te convidou para a [família]". */
  invitedByName?: string;
}

export interface GrupoResponse {
  families: FamilyView[];
  pendingInvitations: PendingFamilyInvite[];
}

export class GrupoError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'GrupoError';
  }
}

async function pedir<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'same-origin',
    ...(init?.body
      ? { headers: { 'content-type': 'application/json', 'idempotency-key': crypto.randomUUID() } }
      : {}),
    ...init,
  });

  const texto = await res.text();
  const corpo = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};

  if (!res.ok) {
    throw new GrupoError(
      String(corpo['error'] ?? 'erro'),
      String(corpo['message'] ?? 'Não foi possível concluir.'),
    );
  }
  return corpo as T;
}

export const readGrupo = (tripId: string) =>
  pedir<GrupoResponse>(`${API}/trips/${tripId}/families`);

export const createFamily = (
  tripId: string,
  body: { name: string; inviteMemberIds: string[] },
) =>
  pedir<{ id: string; invited: number }>(`${API}/trips/${tripId}/families`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const inviteToFamily = (tripId: string, familyId: string, inviteeMemberId: string) =>
  pedir<{ id: string }>(`${API}/trips/${tripId}/families/${familyId}/invitations`, {
    method: 'POST',
    body: JSON.stringify({ inviteeMemberId }),
  });

export const answerInvite = (tripId: string, invitationId: string, accept: boolean) =>
  pedir<{ accepted: boolean }>(`${API}/trips/${tripId}/family-invitations/${invitationId}`, {
    method: 'POST',
    body: JSON.stringify({ accept }),
  });

export const leaveFamily = (tripId: string) =>
  pedir<{ familyId: string }>(`${API}/trips/${tripId}/families/me`, { method: 'DELETE' });

export interface MemberPermissionsView {
  memberId: string;
  userId: string;
  name: string;
  role: 'owner' | 'admin' | 'collaborator' | 'participant';
  overrides: { permission: string; granted: boolean }[];
  effective: string[];
}

export interface PermissionHistoryEntry {
  id: string;
  action: string;
  actorName: string;
  before: { role?: string; effective?: string[] } | null;
  after: { role?: string; effective?: string[]; changed?: string[] } | null;
  createdAt: string;
}

export const readPermissions = (tripId: string, memberId: string) =>
  pedir<{ member: MemberPermissionsView; history: PermissionHistoryEntry[] }>(
    `${API}/trips/${tripId}/members/${memberId}/permissions`,
  );

export const savePermissions = (
  tripId: string,
  memberId: string,
  body: { role: string; permissions: string[] },
) =>
  pedir<MemberPermissionsView>(`${API}/trips/${tripId}/members/${memberId}/permissions`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const removeMember = (tripId: string, memberId: string) =>
  pedir<{ memberId: string; name: string }>(`${API}/trips/${tripId}/members/${memberId}`, {
    method: 'DELETE',
  });

export const transferOwnership = (tripId: string, toUserId: string) =>
  pedir<{ newOwnerUserId: string }>(`${API}/trips/${tripId}/ownership`, {
    method: 'POST',
    body: JSON.stringify({ toUserId, confirmation: 'TRANSFERIR' }),
  });

/* ------------------------------------------------------------ convites */

/**
 * Convite de VIAGEM — não confundir com o de família, acima.
 *
 * `code` na lista vem cru (seis caracteres, sem o "NV-"); a tela formata.
 * O servidor nunca grava `expired`: é a tela que compara `expiresAt` com o
 * relógio. O token só existe dentro de `url`, e só na resposta da criação.
 */
export type TripInviteStatus = 'pending' | 'opened' | 'accepted' | 'revoked' | 'expired';

export interface TripInviteView {
  id: string;
  code: string;
  status: TripInviteStatus;
  expiresAt: string;
  acceptedBy: string | null;
  failedAttempts: number;
  createdAt: string;
}

export interface CreatedTripInvite {
  id: string;
  url: string;
  code: string;
  expiresAt: string;
}

/** O código do grupo em vigor — reutilizável, um por viagem. */
export interface GroupInviteView {
  id: string;
  code: string;
  url: string;
  expiresAt: string;
  uses: number;
  createdAt: string;
}

export type GroupInviteExpiresIn = '1d' | '7d' | '30d' | 'trip';

export const getGroupInvite = (tripId: string) =>
  pedir<{ group: GroupInviteView | null }>(`${API}/trips/${tripId}/invitations/group`);

export const createGroupInvite = (tripId: string, expiresIn: GroupInviteExpiresIn) =>
  pedir<CreatedTripInvite & { kind: 'group' }>(`${API}/trips/${tripId}/invitations`, {
    method: 'POST',
    body: JSON.stringify({ kind: 'group', expiresIn }),
  });

export const listTripInvites = (tripId: string) =>
  pedir<{ invitations: TripInviteView[] }>(`${API}/trips/${tripId}/invitations`);

export const createTripInvite = (tripId: string) =>
  pedir<CreatedTripInvite>(`${API}/trips/${tripId}/invitations`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

export const revokeTripInvite = (tripId: string, invitationId: string) =>
  pedir<Record<string, never>>(`${API}/trips/${tripId}/invitations/${invitationId}`, {
    method: 'DELETE',
  });

/** Renomear e/ou recolorir (item 24): quem está na família, ou quem administra. */
export const updateFamily = (tripId: string, familyId: string, body: { name?: string; color?: string }) =>
  pedir<FamilyView>(`${API}/trips/${tripId}/families/${familyId}`, { method: 'PATCH', body: JSON.stringify(body) });

/** Dissolver: todo mundo sai, a família some das listas. */
export const dissolveFamily = (tripId: string, familyId: string) =>
  pedir<{ id: string; left: number }>(`${API}/trips/${tripId}/families/${familyId}`, { method: 'DELETE' });

